import { useEffect } from "react";
import { X } from "lucide-react";
import { ChatHistory } from "./ChatHistory";

interface Props {
  conv: string;
  open: boolean;
  onClose: () => void;
  onPickSuggestedAction?: (prefill: string) => void;
}

/**
 * Right-side slide-in drawer hosting the full agent transcript.
 *
 * Why a drawer rather than the previous inline panel:
 * - Multi-line markdown answers need vertical room — a 288px panel
 *   pinned under the input clipped them.
 * - The user shouldn't have to choose between seeing their panneaux
 *   and seeing the conversation; a side drawer leaves the panneaux
 *   visible to the left.
 * - Esc + backdrop close is the universal modal pattern (Claude.ai,
 *   ChatGPT, Linear).
 */
export function ChatDrawer({ conv, open, onClose, onPickSuggestedAction }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
      <div
        className="flex-1 bg-black/30 dark:bg-white/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <aside className="w-full max-w-[34rem] bg-white dark:bg-surface-dark shadow-2xl flex flex-col border-l border-black/10 dark:border-white/10 animate-in slide-in-from-right">
        <header className="flex items-center justify-between px-6 py-4 border-b border-black/10 dark:border-white/10">
          <div>
            <h2 className="text-base font-semibold">Conversation avec l'agent</h2>
            <p className="text-xs text-muted dark:text-muted-dark mt-0.5">
              Toutes les propositions et synthèses restent dans le panneau ;
              cette fenêtre montre le dialogue.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-canvas dark:hover:bg-canvas-dark text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark flex items-center justify-center"
            aria-label="Fermer la conversation"
            title="Fermer (Échap)"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <ChatHistory
          conv={conv}
          onPickSuggestedAction={onPickSuggestedAction}
        />
      </aside>
    </div>
  );
}
